/**
 * Token Encryption Service
 *
 * Handles encryption and decryption of JWT tokens using AES-GCM-256. Tokens are encrypted before storage in
 * localStorage to prevent theft.
 *
 * Security Features:
 *
 * - AES-GCM-256 authenticated encryption
 * - PBKDF2 key derivation (100,000 iterations, SHA-256)
 * - Session binding (keys derived from oi_tkn_id claim)
 * - 12-byte IV (recommended for AES-GCM)
 * - 16-byte authentication tag
 *
 * @packageDocumentation
 */

import { Injectable } from '@angular/core';
import { ImpersonationError, ImpersonationErrorCode } from '../models/impersonation-error.model';
import { EncryptedToken, ENCRYPTION_CONFIG, JWTClaims } from '../models/impersonation-token.model';
import * as CryptoUtils from '../utils/crypto.utils';

/**
 * Service for encrypting and decrypting JWT tokens
 *
 * This service uses the Web Crypto API to perform authenticated encryption on JWT tokens before storing them in
 * localStorage. Encryption keys are derived from the session token ID (oi_tkn_id claim) to ensure tokens can only be
 * decrypted within the same session.
 *
 * @example
 *   ```typescript
 *   const encryptionService = inject(TokenEncryptionService);
 *   const sessionId = 'b0aa6791-3ce7-4943-8a07-6e03bfc3761e';
 *
 *   // Encrypt token
 *   const encrypted = await encryptionService.encryptToken(jwtToken, sessionId);
 *
 *   // Decrypt token
 *   const decrypted = await encryptionService.decryptToken(encrypted, sessionId);
 *   ```;
 */
@Injectable({
  providedIn: 'root',
})
export class TokenEncryptionService {
  /**
   * Encrypts a JWT token using AES-GCM
   *
   * The encryption key is derived from the session token ID using PBKDF2. This ensures tokens are session-bound and
   * cannot be decrypted outside the originating session (even if stolen from localStorage).
   *
   * @example
   *   ```typescript
   *   const jwtToken = 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...';
   *   const sessionId = claims.oi_tkn_id;
   *   const encrypted = await encryptToken(jwtToken, sessionId);
   *   localStorage.setItem('token', JSON.stringify(encrypted));
   *   ```;
   *
   * @param token - JWT token string to encrypt
   * @param sessionTokenId - Session token ID from oi_tkn_id claim
   * @returns Encrypted token structure with all components
   * @throws ImpersonationError if encryption fails
   */
  async encryptToken(token: string, sessionTokenId: string): Promise<EncryptedToken> {
    try {
      // Verify Web Crypto API is available
      if (!CryptoUtils.isWebCryptoSupported()) {
        throw new Error('Web Crypto API is not supported in this browser');
      }

      // Generate random salt and IV
      const salt = CryptoUtils.generateRandomBytes(ENCRYPTION_CONFIG.SALT_LENGTH);
      const iv = CryptoUtils.generateRandomBytes(ENCRYPTION_CONFIG.IV_LENGTH);

      // Derive encryption key from session token ID
      const key = await CryptoUtils.deriveKey(
        sessionTokenId,
        salt,
        ENCRYPTION_CONFIG.PBKDF2_ITERATIONS,
        ENCRYPTION_CONFIG.KEY_LENGTH
      );

      // Encrypt the JWT token
      const encryptedBuffer = await CryptoUtils.encryptData(token, key, iv);

      // AES-GCM returns ciphertext + tag together in the buffer
      // Extract the tag (last 16 bytes) for separate storage (optional, for clarity)
      const ciphertextWithTag = new Uint8Array(encryptedBuffer);
      const tagStart = ciphertextWithTag.length - ENCRYPTION_CONFIG.TAG_LENGTH;
      const ciphertext = ciphertextWithTag.slice(0, tagStart);
      const tag = ciphertextWithTag.slice(tagStart);

      // Return encrypted token structure
      return {
        ciphertext: CryptoUtils.arrayBufferToBase64(ciphertext.buffer as ArrayBuffer),
        iv: CryptoUtils.arrayBufferToBase64(iv.buffer as ArrayBuffer),
        tag: CryptoUtils.arrayBufferToBase64(tag.buffer as ArrayBuffer),
        salt: CryptoUtils.arrayBufferToBase64(salt.buffer as ArrayBuffer),
        version: ENCRYPTION_CONFIG.VERSION,
        encryptedAt: new Date().toISOString(),
      };
    } catch (error) {
      throw new ImpersonationError(
        ImpersonationErrorCode.ENCRYPTION_ERROR,
        `Failed to encrypt token: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Decrypts an encrypted JWT token using AES-GCM
   *
   * The decryption key is derived from the session token ID using the same PBKDF2 parameters as encryption. Decryption
   * will fail if:
   *
   * - The session token ID is different (session binding)
   * - The encrypted data was tampered with (authentication tag validation)
   * - The encryption format version is incompatible
   *
   * @example
   *   ```typescript
   *   const encrypted = JSON.parse(localStorage.getItem('token'));
   *   const sessionId = currentClaims.oi_tkn_id;
   *   const jwtToken = await decryptToken(encrypted, sessionId);
   *   ```;
   *
   * @param encryptedToken - Encrypted token structure from encryptToken
   * @param sessionTokenId - Session token ID from oi_tkn_id claim
   * @returns Decrypted JWT token string
   * @throws ImpersonationError if decryption fails
   */
  async decryptToken(encryptedToken: EncryptedToken, sessionTokenId: string): Promise<string> {
    try {
      // Verify Web Crypto API is available
      if (!CryptoUtils.isWebCryptoSupported()) {
        throw new Error('Web Crypto API is not supported in this browser');
      }

      // Verify encryption version compatibility
      if (encryptedToken.version !== ENCRYPTION_CONFIG.VERSION) {
        throw new Error(
          `Incompatible encryption version: ${encryptedToken.version} (expected ${ENCRYPTION_CONFIG.VERSION})`
        );
      }

      // Convert Base64 strings back to ArrayBuffers
      const ciphertext = CryptoUtils.base64ToArrayBuffer(encryptedToken.ciphertext);
      const iv = new Uint8Array(CryptoUtils.base64ToArrayBuffer(encryptedToken.iv));
      const tag = CryptoUtils.base64ToArrayBuffer(encryptedToken.tag);
      const salt = new Uint8Array(CryptoUtils.base64ToArrayBuffer(encryptedToken.salt));

      // Derive decryption key from session token ID
      const key = await CryptoUtils.deriveKey(
        sessionTokenId,
        salt,
        ENCRYPTION_CONFIG.PBKDF2_ITERATIONS,
        ENCRYPTION_CONFIG.KEY_LENGTH
      );

      // Reconstruct full encrypted buffer (ciphertext + tag)
      const ciphertextArray = new Uint8Array(ciphertext);
      const tagArray = new Uint8Array(tag);
      const fullEncrypted = new Uint8Array(ciphertextArray.length + tagArray.length);
      fullEncrypted.set(ciphertextArray);
      fullEncrypted.set(tagArray, ciphertextArray.length);

      // Decrypt the JWT token
      const decrypted = await CryptoUtils.decryptData(fullEncrypted.buffer, key, iv);

      return decrypted;
    } catch (error) {
      throw new ImpersonationError(
        ImpersonationErrorCode.ENCRYPTION_ERROR,
        `Failed to decrypt token: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Parses JWT token claims without validation
   *
   * Note: This only decodes the JWT payload, it does NOT verify the signature. Signature verification must be done by
   * the backend.
   *
   * @param token - JWT token string
   * @returns Parsed JWT claims
   * @throws ImpersonationError if token format is invalid
   */
  parseJWTClaims(token: string): JWTClaims {
    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format (expected 3 parts)');
      }

      // Decode the payload (middle part)
      const payloadBase64 = parts[1];
      const payloadJson = atob(payloadBase64);
      const claims = JSON.parse(payloadJson) as JWTClaims;

      // Validate required claims exist
      if (!claims.userId || !claims.oi_tkn_id) {
        throw new Error('JWT missing required claims (userId or oi_tkn_id)');
      }

      return claims;
    } catch (error) {
      throw new ImpersonationError(
        ImpersonationErrorCode.INVALID_TOKEN,
        `Failed to parse JWT claims: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error instanceof Error ? error : undefined
      );
    }
  }
}
